import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { ArrowLeft, Users, ChevronDown, ChevronUp, MoreVertical, Archive, Trash2 } from 'lucide-react'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Avatar } from '../ui/avatar'
import GroupMessageBubble from './GroupMessageBubble'
import MessageInput from './MessageInput'
import LoadingConversations from './LoadingConversations'
import useGroupMessages from '../../utilities/hooks/useGroupMessages'
import { messagesChannelManager } from '../../channels/messages_channel'
import useAuth from '../../utilities/hooks/useAuth'

function GroupConversationView({ conversation, onBack, onArchive, onDelete }) {
  const { currentUser } = useAuth()
  const { messages, isLoading, sendGroupMessage, addGroupMessage, deleteGroupMessage, fetchGroupMessages } =
    useGroupMessages(conversation?.id)
  const messagesEndRef = useRef(null)
  const [showParticipants, setShowParticipants] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(false)
  const actionsMenuRef = useRef(null)

  const canDelete =
    currentUser?.superuser ||
    currentUser?.facilitiesUsers?.some((fu) => fu.admin)

  useEffect(() => {
    if (conversation?.id) {
      fetchGroupMessages()
    }
  }, [conversation?.id, fetchGroupMessages])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (!showActionsMenu) return
    const handleClickOutside = (e) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) {
        setShowActionsMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showActionsMenu])

  useEffect(() => {
    if (!conversation) return

    const handleChannelMessage = (data) => {
      if (data.type === 'new_group_message' && data.group_conversation_id?.toString() === conversation.id?.toString()) {
        addGroupMessage(data.message)
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }
    }

    messagesChannelManager.addCallback(handleChannelMessage)
    return () => messagesChannelManager.removeCallback(handleChannelMessage)
  }, [conversation, addGroupMessage])

  if (!conversation) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-50 p-8 text-center">
        <p className="text-gray-500">Select a conversation to start messaging</p>
      </div>
    )
  }

  if (isLoading) {
    return <LoadingConversations />
  }

  const attrs = conversation.attributes || {}
  const participantCount = attrs.participantCount || 0
  const participants = attrs.participants || []

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3 p-4">
          <Button onClick={onBack} variant="ghost" size="icon" className="lg:hidden" aria-label="Back to conversations">
            <ArrowLeft className="w-5 h-5" />
          </Button>

          <div className="size-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-purple-600" />
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-gray-900 truncate text-balance">{attrs.title}</h2>
            <button
              onClick={() => setShowParticipants((p) => !p)}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-purple-600 transition-colors"
            >
              <span className="tabular-nums">{participantCount} participants</span>
              {showParticipants ? (
                <ChevronUp className="w-3 h-3" />
              ) : (
                <ChevronDown className="w-3 h-3" />
              )}
            </button>
          </div>

          <Badge variant="secondary" className="bg-purple-50 text-purple-700 border-purple-200 flex-shrink-0">
            Group
          </Badge>

          <div className="relative flex-shrink-0" ref={actionsMenuRef}>
            <Button
              onClick={() => setShowActionsMenu((v) => !v)}
              variant="ghost"
              size="icon"
              aria-label="Group conversation actions"
            >
              <MoreVertical className="w-5 h-5" />
            </Button>
            {showActionsMenu && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <button
                  onClick={() => { setShowActionsMenu(false); onArchive(conversation.id) }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Archive className="w-4 h-4" />
                  Archive
                </button>
                {canDelete && (
                  <button
                    onClick={() => { setShowActionsMenu(false); onDelete(conversation.id, attrs.title) }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Group
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {showParticipants && participants.length > 0 && (
          <div className="px-4 pb-3 border-t border-gray-100">
            <div className="flex flex-wrap gap-2 pt-3 max-h-32 overflow-y-auto">
              {participants.map((p) => (
                <div key={p.id} className="flex items-center gap-1.5 bg-gray-50 rounded-full px-2.5 py-1 text-xs text-gray-700">
                  <Avatar className="size-5 flex-shrink-0">
                    {p.cloudinaryPhotoPublicId ? (
                      <img
                        src={`https://res.cloudinary.com/${process.env.CLOUDINARY_CLOUD_NAME}/image/upload/c_fill,g_face,h_20,w_20/${p.cloudinaryPhotoPublicId}.jpg`}
                        alt={p.preferredName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-purple-200 flex items-center justify-center text-purple-700 font-semibold text-[10px]">
                        {(p.preferredName || p.fullName || '?')[0].toUpperCase()}
                      </div>
                    )}
                  </Avatar>
                  <span className="truncate max-w-[120px]">{p.preferredName || p.fullName}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="size-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <Users className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-700 text-balance">No messages yet</p>
            <p className="text-sm text-gray-500 text-pretty mt-1">Send the first message to this group.</p>
          </div>
        ) : (
          <div className="flex flex-col-reverse gap-1">
            <div ref={messagesEndRef} />
            {messages.map((message) => {
              const senderId = message.attributes?.sender?.id
              const currentUserId = currentUser?.id
              const isOwn = senderId && currentUserId && senderId.toString() === currentUserId.toString()

              return (
                <GroupMessageBubble
                  key={message.id}
                  message={message}
                  isOwn={isOwn}
                  canDelete={canDelete}
                  onDelete={deleteGroupMessage}
                />
              )
            })}
          </div>
        )}
      </div>

      <MessageInput
        conversationId={`group-${conversation.id}`}
        onSendMessage={sendGroupMessage}
      />
    </div>
  )
}

GroupConversationView.propTypes = {
  conversation: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onArchive: PropTypes.func,
  onDelete: PropTypes.func
}

GroupConversationView.defaultProps = {
  onArchive: () => {},
  onDelete: () => {}
}

export default GroupConversationView
